import { useEffect, useRef, useState } from "react";
import Chart from "react-apexcharts";
import { FaInfinity } from "react-icons/fa";

const DonutChart = ({
	usageThisMonth,
	allowanceThisMonth,
	allowancePreviousMonth,
	previousMonth,
	totalUsageThisMonth,
	totalUsagePreviousMonth,
	alerts,
	alertspreviousmonth,
	totalStandard,
	totalStandardPreviousMonth,
	setIsCurrentMonthSelected,
}) => {
	const chartRef = useRef(null);
	const [selectedMonth, setSelectedMonth] = useState("thisMonth");
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const [iconSize, setIconSize] = useState(24);
	// Determine usage and allowance based on selected month
	const usage =
		selectedMonth === "thisMonth" ? parseFloat(usageThisMonth) : parseFloat(previousMonth);
	const allowanceNum =
		selectedMonth === "thisMonth"
			? parseFloat(allowanceThisMonth)
			: parseFloat(allowancePreviousMonth);
	const Totalusage =
		selectedMonth === "thisMonth"
			? parseFloat(totalUsageThisMonth)
			: parseFloat(totalUsagePreviousMonth);

	// Calculate usage percentage and remaining allowance
	const usagePercentage =
		allowanceThisMonth === "UNLIMITED" ? 100 : ((usage / allowanceNum) * 100).toFixed(2);
	//const usagePercentage = ((usage / allowanceNum) * 100).toFixed(2);
	const formattedUsage = usage.toFixed(2);
	const formattedAllowance = allowanceNum.toFixed(2);
	const remainingAllowance = formattedAllowance - formattedUsage;

	// Check if usage exceeds allowance
	const exceeded = remainingAllowance < 0;

	useEffect(() => {
		if (chartRef.current) {
			const height = chartRef.current.offsetHeight;
			setIconSize(Math.max(height * 0.16, 24)); // 15% of the height, minimum size of 24px
		}
	}, []);

	useEffect(() => {
		if (setIsCurrentMonthSelected) {
			setIsCurrentMonthSelected(selectedMonth === "thisMonth");
		}
	}, [selectedMonth]);

	const options = {
		chart: {
			type: "donut",
			height: 270, // Set height directly in options
		},
		labels: ["Usage", "Remaining"],
		colors: exceeded ? ["#ad1010", "#ad1010"] : ["#0E9F6E", "#1B204C"],
		plotOptions: {
			pie: {
				donut: {
					size: "65%",
					labels: {
						show: true,
						value: {
							formatter: (val) => {
								if (allowanceThisMonth === "UNLIMITED") {
									return "";
								}
								return `${parseFloat(val).toFixed(2)}%`;
							},
						},
						total: {
							show: true,
							label: allowanceThisMonth === "UNLIMITED" ? "" : "Usage",
							formatter: () =>
								allowanceThisMonth === "UNLIMITED"
									? ""
									: exceeded
										? "100%"
										: `${((usage / allowanceNum) * 100).toFixed(2)}%`,
						},
					},
				},
			},
		},
		stroke: {
			width: 0.5,
			colors: ["transparent"],
		},
		tooltip: {
			theme: "dark", // Preset dark theme (optional)
			style: {
				fontSize: "14px",
				fontFamily: "Arial, sans-serif",
			},
			y: {
				formatter: (val) => `${val.toFixed(2)} %`,
			},
			custom: ({
				series,
				seriesIndex,
				w,
			}) => `<div style="background:#272B49 ; color: #FFFFFF; padding: 8px; border-radius: 6px;">
                <span className="font-semibold text-base leading-normal tracking-normal" style="font-family: 'Segoe UI', sans-serif; color:#9CA3AF"  >${w.globals.labels[seriesIndex]}:</span>  <span  className="font-bold text-sm leading-none tracking-normal" style="font-family: 'Segoe UI', sans-serif;" > ${series[seriesIndex].toFixed(2)}%</span>
              </div>`,
		},
		dataLabels: {
			enabled: true,
			formatter: (val, opts) => {
				if (opts.seriesIndex === 0) {
					return allowanceThisMonth === "UNLIMITED" ? "" : exceeded ? "100%" : `${val.toFixed(2)}%`;
				} else {
					return "";
				}
			},
		},
		legend: {
			position: "bottom",
			horizontalAlign: "center",
			labels: {
				colors: ["#ffffff"],
			},
		},
		annotations: {
			position: "back",
			yaxis: [
				{
					y: usagePercentage,
					borderColor: "transparent",
					label: {
						borderColor: "transparent",
						style: {
							background: exceeded ? "#FF0000" : "#0E9F6E",
						},
						text: exceeded
							? `Exceeded: ${formattedUsage}GB / ${formattedAllowance}GB`
							: `Usage: ${formattedUsage}GB / ${formattedAllowance}GB`,
					},
				},
			],
		},
	};

	const series = exceeded
		? [100, 0]
		: [
				parseFloat(parseFloat(usagePercentage).toFixed(2)),
				100 - parseFloat(parseFloat(usagePercentage).toFixed(2)),
			];

	const toggleDropdown = () => {
		setIsDropdownOpen(!isDropdownOpen);
	};

	const handleSelectMonth = (month) => {
		setSelectedMonth(month);
		setIsDropdownOpen(false);
	};

	return (
		<div>
			<div className="relative flex items-center">
				{" "}
				<div className="relative" ref={chartRef}>
					<button
						id="dropdownDefaultButton"
						onClick={toggleDropdown}
						className="inline-flex min-h-[40px] items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-blue-gray px-3 font-medium text-light-white text-xs transition-all duration-200 hover:bg-dark-blue focus:outline-none focus:ring-4 focus:ring-dark-blue sm:min-h-[44px] sm:px-4 sm:text-sm md:px-5 md:text-base"
						type="button"
						color="blue"
					>
						{selectedMonth === "thisMonth" ? "This Month" : "Last Month"}
						<svg
							className="h-3 w-3 shrink-0 text-white sm:h-4 sm:w-4 md:h-4 md:w-4"
							aria-hidden="true"
							xmlns="http://www.w3.org/2000/svg"
							fill="none"
							viewBox="0 0 10 6"
						>
							<path
								stroke="currentColor"
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth="2"
								d="m1 1 4 4 4-4"
							/>
						</svg>
					</button>

					{isDropdownOpen && (
						<div
							id="dropdown"
							className="absolute left-0 z-10 mt-2 w-36 divide-y divide-gray-100 rounded-lg bg-dark-blue shadow"
						>
							<ul className="py-2 text-gray-200 text-sm" aria-labelledby="dropdownDefaultButton">
								<li>
									<a
										href="#"
										className="block px-2 py-2 hover:bg-blue-gray hover:text-white"
										onClick={() => handleSelectMonth("thisMonth")}
									>
										This Month
									</a>
								</li>
								<li>
									<a
										href="#"
										className="block px-2 py-2 hover:bg-blue-gray hover:text-white"
										onClick={() => handleSelectMonth("lastMonth")}
									>
										Last Month
									</a>
								</li>
							</ul>
						</div>
					)}
				</div>
			</div>
			<div className="relative" ref={chartRef}>
				<div style={{ height: "270px" }}>
					<Chart options={options} series={series} type="donut" height={270} />{" "}
				</div>
				{allowanceThisMonth === "UNLIMITED" && (
					<div className="absolute inset-0 mb-8 flex items-center justify-center">
						<FaInfinity style={{ fontSize: `${iconSize}px` }} className="text-gray-300" />
					</div>
				)}
			</div>
			<div className="col-span-2 rounded-2xl bg-blue-gray bg-opacity-20 p-4">
				<div className="mb-2 flex justify-between">
					<span className="font-normal text-sm text-white leading-tight">Allowance</span>
					<span className="me-2 rounded bg-blue-gray px-2.5 py-0.5 font-medium text-white text-xs">
						{allowanceThisMonth === "UNLIMITED" ? "Unlimited" : `${formattedAllowance} GB`}
					</span>
				</div>
				<div className="mb-2 flex justify-between">
					<span className="font-normal text-sm text-white leading-tight">Allowance Usage</span>
					<span
						className={`me-2 rounded px-2.5 py-0.5 font-medium text-xs ${exceeded ? "bg-red-900 text-red-300" : "bg-green-cyan text-white"}`}
					>
						{allowanceThisMonth === "UNLIMITED"
							? `${formattedUsage} GB`
							: `${
									formattedAllowance - formattedUsage > 0 ? formattedUsage : formattedAllowance
								} GB`}
					</span>
				</div>
				{allowanceThisMonth === "UNLIMITED" ? (
					""
				) : (
					<div className="mb-2 flex justify-between">
						<span className="font-normal text-sm text-white leading-tight">Remaining</span>
						<span
							className={`me-2 rounded px-2.5 py-0.5 font-medium text-xs ${exceeded ? "bg-red-900 text-red-300" : "bg-custom-blue text-white"}`}
						>
							{exceeded ? "Exceeded" : `${remainingAllowance.toFixed(2)} GB`}
						</span>
					</div>
				)}

				{totalStandard !== undefined && (
					<div className="mb-2 flex justify-between">
						<span className="font-normal text-sm text-white leading-tight">Total standard</span>
						<span className="me-2 rounded bg-blue-gray px-2.5 py-0.5 font-medium text-white text-xs">
							{selectedMonth === "thisMonth"
								? `${parseFloat(totalStandard).toFixed(2)} GB`
								: `${parseFloat(totalStandardPreviousMonth).toFixed(2)} GB`}
						</span>
					</div>
				)}

				{selectedMonth === "thisMonth"
					? alerts &&
						alerts.length > 0 &&
						alerts.map((alert, index) => (
							<div key={index} className="mb-2 flex justify-between">
								<span className="font-normal text-sm text-white leading-tight">
									{alert.threshold} %
								</span>
								<span
									className={`me-2 rounded px-2.5 py-0.5 font-medium text-xs ${
										alert.sent_at === null ? "bg-green-cyan text-white" : "bg-custom-red text-white"
									} `}
								>
									{alert.sent_at === null ? "Not yet" : new Date(alert.sent_at).toLocaleString()}
								</span>
							</div>
						))
					: alertspreviousmonth &&
						alertspreviousmonth.length > 0 &&
						alertspreviousmonth.map((alert, index) => (
							<div key={index} className="mb-2 flex justify-between">
								<span className="font-normal text-sm text-white leading-tight">
									{alert.threshold} %
								</span>
								<span
									className={`me-2 rounded px-2.5 py-0.5 font-medium text-xs ${
										alert.sent_at === null ? "bg-green-cyan text-white" : "bg-custom-red text-white"
									} `}
								>
									{alert.sent_at === null ? "Not yet" : new Date(alert.sent_at).toLocaleString()}
								</span>
							</div>
						))}
			</div>
		</div>
	);
};

export default DonutChart;
